/**
 * FilterPdpToolLinks (FRIDGE-731) — the filter tools from a filter product
 * page: the model hub that lists this filter, the finder's number lookup and
 * the model-number locator. The same link row the model hubs and the brand
 * filter pages carry. Plain component with no server-only imports, so the
 * product page's client shell mounts it from server-computed links
 * (lib/filters/pdp-tool-links.ts). prefetch is off: three links low on a
 * product page should not fetch three more pages for every visit.
 */
import Link from 'next/link'
import type { FilterPdpToolLinks as Links } from '@/lib/filters/pdp-tool-links'

export function FilterPdpToolLinks({ links }: { links: Links }) {
  return (
    <section
      id="filter-pdp-tools"
      className="mt-10 rounded-xl bg-white border border-gray-200 py-8 px-5 md:px-8"
      aria-labelledby="filter-pdp-tools-heading"
      data-testid="filter-pdp-tools"
    >
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Before you buy</p>
      <h2 id="filter-pdp-tools-heading" className="text-xl md:text-2xl font-bold text-gray-900">
        Make sure this filter fits
      </h2>
      <p className="text-sm text-gray-600 mt-1 mb-5">
        Look up your refrigerator&rsquo;s model number or the number printed on your old filter, and
        compare every filter we track for it.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        {links.hubs.map((h) => (
          <Link key={h.href} href={h.href} prefetch={false} className="text-[#0469f0] font-semibold hover:underline">
            {`Every ${h.brand} ${h.code} filter, genuine and compatible →`}
          </Link>
        ))}
        <Link href={links.lookupHref} prefetch={false} className="text-[#0469f0] font-semibold hover:underline">
          Look up a filter or refrigerator model number &rarr;
        </Link>
        <Link href={links.locatorHref} prefetch={false} className="text-[#0469f0] font-semibold hover:underline">
          Where is my refrigerator&rsquo;s model number? &rarr;
        </Link>
      </div>
    </section>
  )
}
